import React, { createContext, useContext, useState, useCallback } from "react";

import {
  AuthorizationType,
  AuthorizationMetadata,
  AuthorizationResult,
} from "./types";
import { AuthorizationManager } from "./AuthorizationManager";

interface GlobalAuthorizationContext {
  check(
    type: AuthorizationType,
    resourceId: string,
    metadata?: AuthorizationMetadata
  ): Promise<boolean>;
  authorize(
    type: AuthorizationType,
    resourceId: string,
    credentials: unknown,
    metadata?: AuthorizationMetadata
  ): Promise<AuthorizationResult>;
  revoke(type: AuthorizationType, resourceId: string): Promise<void>;
  error?: string;
  authorizations: Map<AuthorizationType, Set<string>>;
}

const GlobalAuthorizationContext =
  createContext<GlobalAuthorizationContext | null>(null);

interface GlobalAuthorizationProviderProps {
  children: React.ReactNode;
}

export const GlobalAuthorizationProvider: React.FC<
  GlobalAuthorizationProviderProps
> = ({ children }) => {
  const [authorizations, setAuthorizations] = useState<
    Map<AuthorizationType, Set<string>>
  >(() => new Map());
  const [error, setError] = useState<string>();
  const [manager] = useState(() => new AuthorizationManager());

  /**
   * @private
   * Shared logic to process and add authorizations to state.
   *
   * Private; For working with the authorizations state, see check & revoke.
   */
  // eslint-disable-next-line no-underscore-dangle
  const _addAuthorizationsToState = useCallback((authorizations: string[]) => {
    setAuthorizations((prev) => {
      const next = new Map(prev);

      // Parse keys and add to appropriate type buckets
      authorizations.forEach((key) => {
        const [keyType, keyResourceId] = key.split(":");
        const authType = keyType as AuthorizationType;

        if (!next.has(authType)) {
          next.set(authType, new Set());
        }

        next.get(authType)?.add(keyResourceId);
      });

      return next;
    });
  }, []);

  const isAuthorized = useCallback(
    (type: AuthorizationType, resourceId: string): boolean => {
      // Obtain set of authorized IDs based on type
      const typeAuthorizations = authorizations.get(type);

      // Verify if ID exists in the Set
      return typeAuthorizations?.has(resourceId) ?? false;
    },
    [authorizations]
  );

  const check = useCallback(
    async (
      type: AuthorizationType,
      resourceId: string,
      metadata?: AuthorizationMetadata
    ): Promise<boolean> => {
      const strategy = manager.get(type);
      if (!strategy) {
        throw new Error(`No strategy found for type: ${type}`);
      }
      const checkResult = await strategy.check(
        resourceId,
        isAuthorized,
        metadata
      );

      // Add any new authorizations discovered during check
      if (checkResult.authorizations.length > 0) {
        _addAuthorizationsToState(checkResult.authorizations);
      }

      return checkResult.authorized;
    },
    [manager, isAuthorized, _addAuthorizationsToState]
  );

  const authorize = useCallback(
    async (
      type: AuthorizationType,
      resourceId: string,
      credentials: unknown,
      metadata?: AuthorizationMetadata
    ): Promise<AuthorizationResult> => {
      const strategy = manager.get(type);
      if (!strategy) {
        throw new Error(`No strategy found for type: ${type}`);
      }

      setError(undefined);

      try {
        const authResult = await strategy.authorize(
          resourceId,
          credentials,
          metadata
        );

        // Add authorizations to state using shared logic
        if (authResult.authorizations.length > 0) {
          _addAuthorizationsToState(authResult.authorizations);
        }

        return authResult;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Authorization failed";
        setError(errorMessage);
        throw error;
      }
    },
    [manager, _addAuthorizationsToState]
  );

  const revoke = useCallback(
    async (type: AuthorizationType, resourceId: string): Promise<void> => {
      const strategy = manager.get(type);
      if (!strategy) {
        throw new Error(`No strategy found for type: ${type}`);
      }

      setError(undefined);

      try {
        const keysToRemove = await strategy.revoke(resourceId);
        setAuthorizations((prev) => {
          const next = new Map(prev);

          // Parse keys and remove from appropriate type buckets
          keysToRemove.forEach((key) => {
            const [keyType, keyResourceId] = key.split(":");
            const authType = keyType as AuthorizationType;

            const typeSet = next.get(authType);
            if (typeSet) {
              typeSet.delete(keyResourceId);
              if (typeSet.size === 0) {
                next.delete(authType);
              }
            }
          });

          return next;
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Revocation failed";
        setError(errorMessage);
        throw error;
      }
    },
    [manager]
  );

  const contextValue: GlobalAuthorizationContext = {
    check,
    authorize,
    revoke,
    error,
    authorizations,
  };

  return (
    <GlobalAuthorizationContext.Provider value={contextValue}>
      {children}
    </GlobalAuthorizationContext.Provider>
  );
};

export const useGlobalAuthorization = (): GlobalAuthorizationContext => {
  const context = useContext(GlobalAuthorizationContext);
  if (!context) {
    throw new Error(
      "useGlobalAuthorization must be used within a GlobalAuthorizationProvider"
    );
  }
  return context;
};
